import { ChevronLeft, ChevronRight } from '@untitledui/icons';

import { IconButton } from '@/lib/base/buttons/button-utility';
import { cx } from '@/utils/cx';

/**
 * The circular chevron button that collapses/expands a navigation rail — one
 * implementation shared by `SideNavigation` and `ConversationHistoryPanel`,
 * so the affordance can never drift between the two rails
 * (FRONTEND_PATTERNS § "component composition for visual consistency").
 *
 * Built on the `lib/base` IconButton (react-aria focus/keyboard handling);
 * the circular bordered chip look layers on top via `className`.
 */
export function CollapseToggleButton({
  collapsed,
  onToggle,
  expandLabel,
  collapseLabel,
  className,
}: {
  /** Current state: `true` renders the expand chevron (→), `false` the collapse one (←). */
  collapsed: boolean;
  onToggle: () => void;
  /** Accessible name while collapsed, e.g. "Expand chat history". */
  expandLabel: string;
  /** Accessible name while expanded, e.g. "Collapse chat history". */
  collapseLabel: string;
  /** Layout-only additions (e.g. `shrink-0`); the button's look is fixed here. */
  className?: string;
}) {
  const Chevron = collapsed ? ChevronRight : ChevronLeft;
  return (
    <IconButton
      onClick={onToggle}
      color='tertiary'
      size='xs'
      // Element form (not component form) so the chevron keeps its exact
      // 3.5 size instead of IconButton's data-icon sizing.
      icon={<Chevron className='size-3.5' />}
      aria-label={collapsed ? expandLabel : collapseLabel}
      className={cx(
        'size-7 rounded-full p-0',
        'border border-secondary bg-bg-secondary text-tertiary',
        'hover:bg-bg-secondary_hover hover:text-secondary',
        className
      )}
    />
  );
}
